/* eslint-disable react/no-array-index-key */
import React, { FC, useRef } from 'react'
import { Link } from 'gatsby'
import { Button, ButtonSizes } from 'src/components/ui/Button'
import SectionHeader from 'src/components/ui/SectionHeader'
import StoreButtons from 'src/components/ui/StoreButtons'
import { useAppPromoSectionAnimations } from './animations'

export interface IAppPromoSectionProps {
  className?: string
  translationContent: {
    subtitle: string
    firstPartOfTitle: string
    secondPartOfTitle: string
    sectionName: string
    ctaLink: string
    ctaText: string
    images?: Array<{
      url: string
      alt: string
    }>
    image?: {
      url: string
      alt: string
    }
  }
}

const AppPromoSection: FC<IAppPromoSectionProps> = ({
  className,
  translationContent,
}) => {
  const subtitleRef = useRef<HTMLDivElement>(null)
  const largeTitleRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const imagesContainerRef = useRef<HTMLDivElement>(null)
  const imageRefs = useRef<Array<HTMLDivElement | null>>([])
  const outerContainerRef = useRef<HTMLDivElement>(null)

  const promoImages =
    translationContent.images && translationContent.images.length > 0
      ? translationContent.images
      : translationContent.image
      ? [translationContent.image]
      : []

  useAppPromoSectionAnimations(
    subtitleRef,
    largeTitleRef,
    sectionRef,
    imagesContainerRef,
    imageRefs,
    outerContainerRef,
    promoImages.length,
    ctaRef
  )

  imageRefs.current = []

  const isExternalCta = translationContent.ctaLink
    ? /^(https?:)?\/\//.test(translationContent.ctaLink)
    : false
  const isAnchorCta = translationContent.ctaLink
    ? translationContent.ctaLink.startsWith('#')
    : false

  const handleAnchorClick = (e: React.MouseEvent) => {
    if (translationContent.ctaLink?.startsWith('#')) {
      const targetElement = document.querySelector(translationContent.ctaLink)
      if (targetElement && (window as any).lenis) {
        e.preventDefault()
        ;(window as any).lenis.scrollTo(targetElement, { duration: 1.5, offset: 0 })
      }
    }
  }

  const renderCtaButton = () => {
    if (!translationContent.ctaText || !translationContent.ctaLink) return null

    const button = (
      <Button className="w-full md:w-auto" size={ButtonSizes.large}>
        {translationContent.ctaText}
      </Button>
    )

    if (isExternalCta) {
      return (
        <a
          href={translationContent.ctaLink}
          target="_blank"
          rel="noreferrer"
          className="flex w-full md:w-auto"
        >
          {button}
        </a>
      )
    }

    if (isAnchorCta) {
      return (
        <a
          href={translationContent.ctaLink}
          className="flex w-full md:w-auto"
          onClick={handleAnchorClick}
        >
          {button}
        </a>
      )
    }

    return (
      <Link to={translationContent.ctaLink} className="flex w-full md:w-auto">
        {button}
      </Link>
    )
  }

  return (
    <div
      ref={outerContainerRef}
      data-name={translationContent.sectionName}
      id={translationContent.sectionName}
      className={`${className} container py-20 md:py-32 flex flex-col items-stretch overflow-visible bg-transparent`}
    >
      {/* HEADER SECTION: SUBTITLE & TITLE */}
      <SectionHeader
        subtitle={translationContent.subtitle}
        titlePart1={translationContent.firstPartOfTitle}
        titlePart2={translationContent.secondPartOfTitle}
        subtitleRef={subtitleRef}
        titleRef={largeTitleRef}
      />

      {/* CTA & DESKTOP STORE BUTTONS ROW */}
      <div
        ref={ctaRef}
        className="mt-10 md:mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 w-full opacity-0"
      >
        {renderCtaButton()}

        {/* Store Buttons - Desktop Only */}
        <div className="hidden md:block">
          <StoreButtons />
        </div>
      </div>

      {/* IMAGE SECTION - PINNED SCROLL EFFECT */}
      <div
        ref={sectionRef}
        className="mt-12 w-full relative flex flex-col items-center overflow-visible"
      >
        <div
          ref={imagesContainerRef}
          className="w-full relative aspect-2/3 md:aspect-video overflow-hidden"
        >
          {promoImages.map((img, index) => (
            <div
              key={index}
              ref={(el) => {
                imageRefs.current[index] = el
              }}
              className="absolute inset-0 w-full h-full rounded-32 overflow-hidden"
              style={{ zIndex: index + 1, willChange: 'transform, border-radius', backfaceVisibility: 'hidden' }}
            >
              <img
                src={img.url}
                alt={img.alt || `Promo image ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Store Buttons - Mobile Only */}
      <div className="md:hidden mt-8 flex justify-center">
        <StoreButtons className="justify-center" />
      </div>
    </div>
  )
}

AppPromoSection.defaultProps = {
  className: '',
}

export default AppPromoSection
